import React, {memo, useCallback, useEffect, useState} from 'react';
import './index.scss';

import iconAlarmHome from '../../assets/images/icons/alarm/alarm-home.png';
import iconAlarmOutside from '../../assets/images/icons/alarm/alarm-outside.png';
import iconCancel from '../../assets/images/icons/alarm/cancel.png';
import iconAbort from '../../assets/images/icons/alarm/abort.png';
import iconOK from '../../assets/images/icons/alarm/ok.png';
import iconHome from '../../assets/images/icons/alarm/home.png';
import iconOutside from '../../assets/images/icons/alarm/outside.png';
import iconTime from '../../assets/images/icons/alarm/time.png';
import {useRPC} from '../../hooks/rpc';
import {useNumpad} from '../../hooks/numpad';
import {useDispatch, useSelector} from 'react-redux';
import {useInterval} from '../../hooks/interval';
import {useMessage} from '../../hooks/message';
import {onClickHandler} from '../../utils/onclick';
import {Container} from '../../components/Container';
import {useSetSelector} from '../../hooks/set-selector';
import {ALARM_VALUE_HOME, ALARM_VALUE_OUTSIDE} from '../../types/control';
import {alarmSettingUpAction, backButtonToggle, groupSettingAction} from '../../store/ui/actions';
import {useLocalization} from "../../hooks/localization";

export const AlarmScreen = memo(function AlarmScreen() {
    const {t} = useLocalization();
    const defaultAlarmState = useSelector(state => state.control.alarmState.default);
    const sensorTypeSet = useSetSelector(state => state.control.sensorTypeList);
    const alarmSettingUp = useSelector(state => state.ui.alarmSettingUp);
    const [rpc] = useRPC();
    const dispatch = useDispatch();
    dispatch(backButtonToggle({is_open: true, to: '/'}));
    const config = useSelector(state => state.control.config);

    const [isHome, setIsHome] = useState<boolean>(defaultAlarmState.level === 1 ? null : (defaultAlarmState.level === 2));
    const [isFlicking, setIsFlicking] = useState(false);
    const [isSet, setIsSet] = useState(false);
    const [isAborted, setIsAborted] = useState(false);
    const [isSetComplete, setIsSetComplete] = useState(false);
    const [currentTimer, setCurrentTimer] = useState(60);
    const [renderNumpad, password, setPassword] = useNumpad(isAborted);
    const {failure} = useMessage();

    const homeTimer = config.alarm_settings.home_timer_seconds;
    const outsideTimer = config.alarm_settings.outside_timer_seconds;
    const isRinging = defaultAlarmState.will_ring || defaultAlarmState.ringing;

    useEffect(()=>{
        rpc.clickSoundOn()
        dispatch(groupSettingAction({visible: false}));
    },[])

    useEffect(() => {
        setIsHome(defaultAlarmState.level === 1 ? null : (defaultAlarmState.level === 2));
    }, [defaultAlarmState]);

    useEffect(() => {
        let interval;
        if (isRinging) {
            interval = setInterval(() => {
                setIsFlicking(prevState => !prevState);
            }, 250);
        }
        return () => {
            clearInterval(interval);
        };
    }, [isRinging]);

    useInterval(() => {
        if (isSet && !isSetComplete) {
            if (currentTimer > 0) {
                setCurrentTimer(value => value - 1);
            } else {
                setIsSetComplete(true);
                rpc.setAlarm(isHome ? ALARM_VALUE_HOME : ALARM_VALUE_OUTSIDE, '')
                    .catch(failure);
            }
        } else {
            if (alarmSettingUp) {
                dispatch(alarmSettingUpAction(false));
            }
        }
    }, 1000);

    useEffect(() => {
        setCurrentTimer(isHome ? homeTimer : outsideTimer);
    }, [isSet, isHome, homeTimer, outsideTimer]);

    useEffect(() => {
        const level = defaultAlarmState.level;
        if (level === 3 || level === 2) {
            setIsSetComplete(true);
            setIsSet(true);
            setIsAborted(false);
        } else if (level === 1) {
            setIsSetComplete(false);
            setIsSet(false);
            setIsAborted(false);
        } else {
            setIsSetComplete(false);
            setIsSet(false);
            setIsAborted(true);
        }
    }, [defaultAlarmState]);

    const handlePassword = useCallback(done => {
        if (!done) {
            failure(t('pls_enter_a_valid_password'));
        } else {
            setPassword('');
        }
    }, [t, failure, setPassword]);

    const set = useCallback(() => {
        if (isRinging) {
            rpc.setAlarm(1, password).then(handlePassword);
        } else if (isSet) {
            if (!isSetComplete) {
                setIsSet(false);
                dispatch(alarmSettingUpAction(false));
            } else {
                rpc.setAlarm(1, password).then(handlePassword);
            }
        } else {
            setIsSet(true);
            setIsSetComplete(false);
            dispatch(alarmSettingUpAction(true));
        }

        setPassword('');
    }, [handlePassword, isRinging, isSet, isSetComplete, password, rpc, setPassword, dispatch]);

    const setHome = useCallback((isHome: boolean) => {
        if (isSet) {
            return;
        }

        setIsHome(isHome);
        set();
    }, [isSet, set]);

    const abort = useCallback(() => {
        if (isAborted) {
            rpc.setAlarm(1, '');
        } else {
            rpc.setAlarm(0, password).then(handlePassword);
        }
    }, [handlePassword, isAborted, password, rpc]);

    const showHomeButton = !isRinging && (isHome || !isSet);
    const homeButtonImage = !isSet ? '' : (isSetComplete ? 'active' : 'passive');

    const showOutsideButton = !isRinging && (!isHome || !isSet);
    const outsideButtonImage = !isSet ? '' : (isSetComplete ? 'active' : 'passive');

    const showRingingButton = isRinging;

    const showCancelButton = isSet || isRinging;
    const cancelButtonImage = (isRinging && !isFlicking) ? '' : ' abort';

    const showTimerPanel = !isRinging && (!isSet || !isSetComplete);

    return (
        <Container>
            <div className={'page-wrapper alarm'}>
                <div className="setup">
                    <div className={'buttons' + (isAborted ? ' aborted' : '')}>
                        {
                            renderIf(isAborted)(() =>
                                    <div {...onClickHandler(abort)} className="alarm-setup-button">
                                        <div className='image set'>
                                            <img src={iconOK} alt=''/>
                                        </div>
                                        <span>{t('re_activate')}</span>
                                    </div>,
                                () =>
                                    <>
                                        {
                                            renderIf(showHomeButton)(() =>
                                                <div {...onClickHandler(() => setHome(true))}
                                                     className="alarm-setup-button">
                                                    <div
                                                        className={'image ' + homeButtonImage}>
                                                        <img src={iconAlarmHome} alt=''/>
                                                    </div>
                                                    <span>{isSetComplete === true ? t('at_home_installed') : t('at_home_install')}</span>
                                                </div>,
                                            )
                                        }
                                        {
                                            renderIf(showOutsideButton)(() =>
                                                <div {...onClickHandler(() => setHome(false))}
                                                     className="alarm-setup-button">
                                                    <div
                                                        className={'image ' + outsideButtonImage}>
                                                        <img src={iconAlarmOutside} alt=''/>
                                                    </div>
                                                    <span>{isSetComplete === true ? t('at_out_installed') : t('at_out_install')}</span>
                                                </div>,
                                            )
                                        }
                                        {
                                            renderIf(showRingingButton)(() =>
                                                <div className="alarm-setup-button">
                                                    <div
                                                        className='image abort'>
                                                        <img src={iconAlarmOutside} alt=''/>
                                                    </div>
                                                    <span>{t('alarm_run')}</span>
                                                </div>,
                                            )
                                        }
                                        {
                                            renderIf(showCancelButton)(() =>
                                                <div {...onClickHandler(set)} className="alarm-setup-button">
                                                    <div
                                                        className={'image' + cancelButtonImage}>
                                                        <img src={iconCancel} alt=''/>
                                                    </div>
                                                    <span>{t('cancel_the_alarm')}</span>
                                                </div>,
                                            )
                                        }
                                        {
                                            <div {...onClickHandler(abort)} className="alarm-setup-button">
                                                <div className='image abort'>
                                                    <img src={iconAbort} alt=''/>
                                                </div>
                                                <span>{t('disable')}</span>
                                            </div>
                                        }
                                    </>,
                            )
                        }
                    </div>
                    {
                        renderIf(showTimerPanel)(() =>
                                <div className="timer">
                                    <div className="timer-setup">
                                        <span>{t('TIMER_TIME')}</span>
                                        <div className="timer-rows">
                                            <div className="timer-row">
                                                <div className="timer-image">
                                                    <img src={iconHome} alt=''/>
                                                </div>
                                                <span className="timer-input">{homeTimer}</span>
                                            </div>
                                            <div className="timer-row">
                                                <div className="timer-image">
                                                    <img src={iconOutside} alt=''/>
                                                </div>
                                                <span className="timer-input">{outsideTimer}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <img src={iconTime} alt=''/>
                                    <div className="timer-counter">
                                        <span>{t('TIME_REMAINING')}</span>
                                        <span className="timer-countdown">{!isSet ? '--' : currentTimer}</span>
                                    </div>
                                </div>,
                            () => defaultAlarmState.latest_log_list.length > 0 ?
                                <div className="timer info">
                                    {
                                        defaultAlarmState.latest_log_list.map((log, index) => {
                                            const dateObject = new Date(log.timestamp * 1000);
                                            let hourString = dateObject.getHours().toString();
                                            if (hourString.length === 1) {
                                                hourString = '0' + hourString;
                                            }

                                            let minuteString = dateObject.getMinutes().toString();
                                            if (minuteString.length === 1) {
                                                minuteString = '0' + minuteString;
                                            }

                                            const timeString = `${hourString}.${minuteString}`;

                                            return (
                                                <div key={`alarm_log_${index.toString()}`} className="information">
                                                    <img src={sensorTypeSet[log.sensor_type_id].image_set.default}
                                                         alt=''/>
                                                    <span>{timeString} &nbsp;&nbsp;{log.log_message}</span>
                                                </div>
                                            );
                                        })
                                    }
                                </div> : null,
                        )}
                </div>
                {
                    renderNumpad()
                }
            </div>
        </Container>
    );
});
